import {
  isHtmlSupport,
  type LearningSupportEntity,
  LearningSupportSchema
} from '@/core/infrastructure/schemas/learning/support/LearningSupportSchema'
import { LearningSupportType } from '@/core/domain/resources/enums/LearningSupport'
import { EntityValidator } from '@/core/infrastructure/utils/EntityValidator.ts'
import type { UploadedSupportFile } from '@/core/infrastructure/schemas/learning/support/UploadedSupportFileSchema.ts'

const GENIALLY_URL_REGEX = /^https:\/\/view\.genially\.(com|ly)\/\S+$/i

export class LearningSupport {
  id: number
  type: LearningSupportType.Values
  url: string
  estimatedTime: number
  order: number
  // Renseignés uniquement pour un support HTML, tels que renvoyés par l'upload
  storageKey: string | null
  originalFileName: string | null
  fileSize: number | null

  constructor(support: LearningSupportEntity) {
    this.id = support.id
    this.type = support.type
    this.url = support.url
    this.estimatedTime = support.estimatedTime
    this.order = support.order
    this.storageKey = isHtmlSupport(support) ? support.storageKey : null
    this.originalFileName = isHtmlSupport(support) ? (support.originalFileName ?? null) : null
    this.fileSize = isHtmlSupport(support) ? (support.fileSize ?? null) : null
  }

  // Obtenir le libellé du type de support
  getTypeLabel(): string {
    return LearningSupportType.label(this.type)
  }

  // Vérifier si le support est de type Genially
  isGenially(): boolean {
    return this.type === LearningSupportType.Values.GENIALLY
      && GENIALLY_URL_REGEX.test(this.url)
  }

  // Vérifier si le support est un fichier HTML
  isHtml(): boolean {
    return this.type === LearningSupportType.Values.HTML
  }

  // Un support Genially doit avoir une URL Genially, un support HTML un fichier uploadé
  isValid(): boolean {
    if (this.isHtml()) {
      return this.url !== '' && !!this.storageKey
    }
    return this.isGenially()
  }

  // Changer de type vide le contenu : une URL Genially n'a pas de sens pour un fichier et inversement
  changeType(type: LearningSupportType.Values): void {
    if (type === this.type) return

    this.type = type
    this.url = ''
    this.storageKey = type === LearningSupportType.Values.HTML ? '' : null
    this.originalFileName = null
    this.fileSize = null
  }

  // Rattacher le fichier uploadé, sans recalculer url ni storageKey (l'API les vérifie au PUT)
  attachUploadedFile(file: UploadedSupportFile): void {
    this.url = file.url
    this.storageKey = file.storageKey
    this.originalFileName = file.originalFileName
    this.fileSize = file.fileSize
  }

  // Formatter la taille du fichier HTML en Ko ou Mo
  getFormattedFileSize(): string {
    if (this.fileSize === null) return ''

    const kiloBytes = this.fileSize / 1024
    if (kiloBytes < 1024) {
      return `${Math.max(1, Math.round(kiloBytes))} Ko`
    }
    return `${(kiloBytes / 1024).toFixed(1).replace('.', ',')} Mo`
  }

  // Formatter le temps estimé en format heures:minutes
  getFormattedEstimatedTime(): string {
    const hours = Math.floor(this.estimatedTime / 60)
    const minutes = this.estimatedTime % 60

    if (hours > 0) {
      return `${hours}h${minutes > 0 ? ` ${minutes}min` : ''}`
    } else {
      return `${minutes}min`
    }
  }

  // Convertir en format API pour les requêtes
  toApiFormat() {
    const base = {
      id: this.id,
      type: this.type,
      url: this.url,
      estimatedTime: this.estimatedTime,
      order: this.order
    }

    if (!this.isHtml()) {
      return base
    }

    // url et storageKey sont renvoyées telles que l'upload les a rendues : l'API les vérifie
    return {
      ...base,
      storageKey: this.storageKey,
      originalFileName: this.originalFileName,
      fileSize: this.fileSize
    }
  }

  public static createEmpty(
    type: LearningSupportType.Values = LearningSupportType.Values.GENIALLY
  ): LearningSupport {
    const base = { id: 0, url: '', estimatedTime: 0, order: 0 }

    if (type === LearningSupportType.Values.HTML) {
      return new LearningSupport({ ...base, type, storageKey: '' })
    }
    return new LearningSupport({ ...base, type })
  }

  public static create(data: unknown): LearningSupport {
    return EntityValidator.validateAndCreate(
      LearningSupportSchema,
      data,
      LearningSupport,
      'LearningSupport'
    )
  }
}
